import { useEffect, useReducer, useRef } from 'react';

/**
 * `holding`, for at most `maxMs` of it being continuously true.
 *
 * The clock starts the first render `holding` is true and keeps running while it
 * stays true, whatever else re-renders; it resets once `holding` goes false. A
 * timer re-renders at the deadline, so the hold lapses even if nothing else
 * changes. Used to bound a "rebalance in flight" hold that two separate signals
 * keep alive on clocks of their own (EN-10625).
 */
export const useBoundedHold = (holding: boolean, maxMs: number): boolean => {
  const startedAtRef = useRef<number | null>(null);
  if (!holding) startedAtRef.current = null;
  else if (startedAtRef.current === null) startedAtRef.current = Date.now();
  const startedAt = startedAtRef.current;

  const [, recheck] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (startedAt === null) return undefined;
    const remaining = startedAt + maxMs - Date.now();
    if (remaining <= 0) return undefined;
    const timer = setTimeout(recheck, remaining + 1);
    return () => clearTimeout(timer);
  }, [startedAt, maxMs]);

  return startedAt !== null && Date.now() - startedAt < maxMs;
};

/**
 * The tables the grid counts. While a rebalance hold is live, the larger of its
 * cells and the tournament's slot count (a move in flight still counts); once
 * it has lapsed, only the cells it shows.
 */
export const gridCountWithRebalanceHold = (
  cellCount: number,
  layoutCount: number,
  holdLive: boolean
): number => (holdLive ? Math.max(cellCount, layoutCount) : cellCount);
